import React, { useEffect, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { getUsageStats } from '../services/api';

interface UsageStats {
  total_files: number;
  total_pages_quota: number;
  total_pages_used: number;
  total_pages_remaining: number;
  document_types: {
    [key: string]: number;
  };
  last_3_days: Array<{
    date: string;
    files_processed: number;
    pages_processed: number;
  }>;
}

const UsagePage: React.FC = () => {
  const { darkMode } = useTheme();
  const [usageStats, setUsageStats] = useState<UsageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUsageStats = async () => {
      try {
        const orgId = localStorage.getItem('orgId'); // Get organization ID from localStorage
        const token = localStorage.getItem('token'); // Get authentication token
        
        if (!token) {
          throw new Error('Authentication token not found. Please log in again.');
        }
        
        // Use the organization ID if available, otherwise it will use the user's default organization
        const data = await getUsageStats(orgId || '');
        setUsageStats(data);
      } catch (err) {
        console.error('Error fetching usage stats:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch usage stats');
      } finally {
        setLoading(false);
      }
    };

    fetchUsageStats();
  }, []);

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  if (error) {
    return <div className="text-red-500">Error: {error}</div>;
  }

  if (!usageStats) {
    return <div>No data available</div>;
  }
  
  return (
    <div>
      <h1 className={`text-2xl font-semibold mb-6 ${darkMode ? 'text-white' : ''}`}>Usage</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div className={`${darkMode ? 'bg-gray-800 text-white' : 'bg-white'} p-6 rounded-lg shadow`}>
          <h2 className={`text-lg font-medium ${darkMode ? 'text-gray-100' : 'text-gray-900'} mb-4`}>Total Files</h2>
          <div className="flex items-center justify-center">
            <div className="text-4xl font-bold text-primary">{usageStats.total_files}</div>
          </div>
          <div className="text-center mt-2">
            <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Documents processed</span>
          </div>
        </div>
        
        <div className={`${darkMode ? 'bg-gray-800 text-white' : 'bg-white'} p-6 rounded-lg shadow`}>
          <h2 className={`text-lg font-medium ${darkMode ? 'text-gray-100' : 'text-gray-900'} mb-4`}>Total Pages Quota</h2>
          <div className="flex items-center justify-center">
            <div className="text-4xl font-bold text-primary">{usageStats.total_pages_quota}</div>
          </div>
          <div className={`w-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded-full h-2.5 mt-4`}>
            <div className="bg-primary h-2.5 rounded-full" style={{ width: `${(usageStats.total_pages_used / usageStats.total_pages_quota) * 100}%` }}></div>
          </div>
          <div className="flex justify-between mt-2">
            <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{usageStats.total_pages_used} pages used</span>
            <span className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{usageStats.total_pages_remaining} pages remaining</span>
          </div>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className={`${darkMode ? 'bg-gray-800 text-white' : 'bg-white'} p-6 rounded-lg shadow`}>
          <h2 className={`text-lg font-medium ${darkMode ? 'text-gray-100' : 'text-gray-900'} mb-4`}>Documents by Type</h2>
          <div className="space-y-4">
            {Object.entries(usageStats.document_types).map(([type, percentage]) => (
              <div key={type}>
                <div className="flex justify-between mb-1">
                  <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {type.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                  </span>
                  <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {percentage.toFixed(1)}%
                  </span>
                </div>
                <div className={`w-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded-full h-2`}>
                  <div className={`${
                    type === 'invoice' ? 'bg-blue-500' :
                    type === 'bank_statement' ? 'bg-green-500' :
                    type === 'vat_book' ? 'bg-yellow-500' :
                    'bg-red-500'
                  } h-2 rounded-full`} style={{ width: `${percentage}%` }}></div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className={`${darkMode ? 'bg-gray-800 text-white' : 'bg-white'} p-6 rounded-lg shadow`}>
          <h2 className={`text-lg font-medium ${darkMode ? 'text-gray-100' : 'text-gray-900'} mb-4`}>Last 3 Days Usage</h2>
          <div className="space-y-4">
            {usageStats.last_3_days.map((day, index) => {
              const dayLabel = index === 0 ? 'Today' :
                             index === 1 ? 'Yesterday' :
                             '2 days ago';
              const maxPages = Math.max(...usageStats.last_3_days.map(d => d.pages_processed));
              const percentage = (day.pages_processed / maxPages) * 100;
              
              return (
                <div key={day.date}>
                  <div className="flex justify-between mb-1">
                    <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{dayLabel}</span>
                    <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{day.pages_processed} pages</span>
                  </div>
                  <div className={`w-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded-full h-2`}>
                    <div className="bg-primary h-2 rounded-full" style={{ width: `${percentage}%` }}></div>
                  </div>
                  <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>{day.files_processed} files processed</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsagePage;